/**
 * QUEUE REORDER UTILITIES
 * 
 * Funções imutáveis para reordenação de fila
 * Garantem: sem mutação do array original, atualização de positions, sem race conditions
 */

import { SellerQueueItem } from '../types'

/**
 * Função genérica de reordenação por índice
 * Move um item de umIndex para outroIndex e recalcula todas as positions
 * 
 * @param queue Array original (não é mutado)
 * @param fromIndex Índice atual do item
 * @param toIndex Índice destino
 * @returns Novo array reordenado com positions recalculadas (1, 2, 3...)
 * 
 * @example
 * const newQueue = reorderQueue(queue, 2, 0)  // Move índice 2 para o início
 */
export function reorderQueue(
  queue: SellerQueueItem[],
  fromIndex: number,
  toIndex: number
): SellerQueueItem[] {
  // Validação
  if (fromIndex < 0 || fromIndex >= queue.length) return queue
  if (toIndex < 0 || toIndex >= queue.length) return queue
  if (fromIndex === toIndex) return queue

  // 1️⃣ Criar cópia (imutável)
  const newQueue = [...queue]

  // 2️⃣ Remover item da posição original
  const [movedItem] = newQueue.splice(fromIndex, 1)

  // 3️⃣ Inserir na nova posição
  newQueue.splice(toIndex, 0, movedItem)

  // 4️⃣ Recalcular positions sequencialmente (1, 2, 3...)
  const reorderedQueue = newQueue.map((item, idx) => ({
    ...item,
    position: idx + 1
  }))

  return reorderedQueue
}

/**
 * Move item para CIMA (diminui posição)
 * @param queue Array original
 * @param index Índice atual do item
 * @returns Novo array ou array original se não puder mover
 */
export function moveItemUp(
  queue: SellerQueueItem[],
  index: number
): SellerQueueItem[] {
  if (index <= 0) return queue  // Não pode mover se já está no topo
  return reorderQueue(queue, index, index - 1)
}

/**
 * Move item para BAIXO (aumenta posição)
 * @param queue Array original
 * @param index Índice atual do item
 * @returns Novo array ou array original se não puder mover
 */
export function moveItemDown(
  queue: SellerQueueItem[],
  index: number
): SellerQueueItem[] {
  if (index >= queue.length - 1) return queue  // Não pode mover se já está no final
  return reorderQueue(queue, index, index + 1)
}

/**
 * Validação: Prepara dados para enviar ao backend
 * Extrai apenas sellerId e position
 * 
 * @param queue Array reordenado
 * @returns Array de updates para backend
 */
export function prepareBackendUpdates(queue: SellerQueueItem[]) {
  return queue.map(item => ({
    sellerId: item.sellerId,
    position: item.position
  }))
}

/**
 * Exemplo de uso com TypeScript:
 * 
 * const [localQueue, setLocalQueue] = useState<SellerQueueItem[]>([])
 * 
 * const handleMoveUp = (index: number) => {
 *   const newQueue = moveItemUp(localQueue, index)
 *   if (newQueue !== localQueue) {  // Verificar se realmente mudou
 *     setLocalQueue(newQueue)
 *     const updates = prepareBackendUpdates(newQueue)
 *     batchReorderQueue(updates)  // Enviar ao backend
 *   }
 * }
 */
